import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categoryData, getCategoryBySlug } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const cat = getCategoryBySlug(article.category);
  const relatedArticles = getRelatedArticles(article, 4);

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        extra={{ name: `${article.title} - 久久网`, description: article.excerpt }}
      />
      <SchemaOrg
        type="Article"
        extra={{
          title: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: article.author,
          url: `https://njqxb.cn/article/${article.slug}`,
          imageUrl: article.imageUrl,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        extra={{
          items: [
            { name: '首页', url: 'https://njqxb.cn/' },
            { name: cat?.name || article.category, url: `https://njqxb.cn/category/${article.category}` },
            { name: article.title, url: `https://njqxb.cn/article/${article.slug}` },
          ],
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', url: '/' },
                { name: cat?.name || article.category, url: `/category/${article.category}` },
                { name: article.title },
              ]}
            />

            <article className="mt-8">
              <div className="rounded-xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-[320px] md:h-[420px] object-cover object-top"
                />
              </div>

              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  {cat && (
                    <Link
                      to={`/category/${cat.id}`}
                      className="px-3 py-1 bg-primary-100/70 text-primary-700 text-xs font-medium rounded-full no-underline hover:bg-primary-200 transition-colors"
                    >
                      {cat.name}
                    </Link>
                  )}
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()}次阅读
                  </span>
                </div>

                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-[var(--font-heading)] leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </header>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-semibold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:text-foreground-700 prose-p:mb-4
                  [&_h2]:font-[var(--font-heading)]"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 p-6 bg-background-100 rounded-xl">
                <p className="text-sm text-foreground-500">
                  <strong className="text-foreground-700">免责声明：</strong>
                  本文仅代表作者个人观点，不代表久久网立场。如涉及版权或其他问题，请通过关于我们页面与我们联系。
                </p>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-12 mb-16">
                <h2 className="text-xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="bg-white rounded-xl border border-background-200/70 overflow-hidden group transition-all duration-300 hover:border-primary-200 no-underline"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={ra.imageUrl}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}